import React, { useState } from 'react';
import { Users, Building, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

const UserTypeSelection = ({ onUserTypeSelected }) => {
  const [selectedType, setSelectedType] = useState(null);

  const userTypes = [
    {
      id: 'trainer',
      title: 'I am a Trainer',
      description: 'Individual trainer providing training services',
      icon: Users,
      features: [
        'Manage training opportunities',
        'Client booking management', 
        'Proposal templates & contracts',
        'Revenue analytics & billing',
        'Training materials library'
      ]
    },
    {
      id: 'organization',
      title: 'I represent an Organization',
      description: 'Company or organization seeking training services',
      icon: Building,
      features: [
        'Post training requirements',
        'Receive & manage proposals',
        'Manage hired trainers',
        'Purchase order tracking',
        'Communication with trainers'
      ]
    }
  ];

  const handleSubmit = () => {
    if (selectedType) {
      onUserTypeSelected(selectedType);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Welcome to TrainerLinked.in Business Hub
          </h1>
          <p className="text-lg text-gray-600">
            Please select your account type to customize your experience
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {userTypes.map((type) => {
            const Icon = type.icon;
            const isSelected = selectedType === type.id;
            
            return (
              <Card 
                key={type.id}
                className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                  isSelected 
                    ? 'border-blue-500 ring-2 ring-blue-200 shadow-lg' 
                    : 'border-gray-200 hover:border-blue-300'
                }`}
                onClick={() => setSelectedType(type.id)}
              >
                <CardHeader className="text-center pb-4">
                  <div className="mx-auto mb-4 relative">
                    <div className={`p-4 rounded-full ${
                      isSelected ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <Icon className={`w-8 h-8 ${
                        isSelected ? 'text-blue-600' : 'text-gray-600'
                      }`} />
                    </div>
                    {isSelected && (
                      <CheckCircle className="absolute -top-1 -right-1 w-6 h-6 text-green-500 bg-white rounded-full" />
                    )}
                  </div>
                  <CardTitle className={`text-xl ${
                    isSelected ? 'text-blue-900' : 'text-gray-900'
                  }`}>
                    {type.title}
                  </CardTitle>
                  <p className="text-gray-600 text-sm">
                    {type.description}
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-900 text-sm mb-3">
                      Key Features:
                    </h4>
                    {type.features.map((feature, index) => (
                      <div key={index} className="flex items-center text-sm text-gray-600">
                        <div className={`w-1.5 h-1.5 rounded-full mr-3 ${
                          isSelected ? 'bg-blue-500' : 'bg-gray-400'
                        }`} />
                        {feature}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center">
          <Button
            onClick={handleSubmit}
            disabled={!selectedType}
            size="lg"
            className="px-8 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue with {selectedType === 'trainer' ? 'Trainer' : selectedType === 'organization' ? 'Organization' : 'Selected'} Account
          </Button>
          
          {selectedType && (
            <p className="mt-4 text-sm text-gray-500">
              You can change this later in your account settings
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserTypeSelection;